import { Preferences, Topic, User, UserPreferences } from '@/types';
import { QueryStatus } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';

import { createUseContext } from '../../../utils/create-use-context';
import { SubscriptionSettings } from '../../admin/managers/subscriptions/data';
import { useAskBluejApiContext } from '../api/context';
import { useUserPreferencesLoaderContext } from '../app/user-preferences-loader';
import { useSubscriptionSettingsQuery } from '../manage-subscription/useSubscriptionSettingsQuery';
import { useSentryContext } from '../sentry/context';
import { useUpdateUserPreferencesQuery } from '../user-preferences/useUpdateUserPreferencesQuery';
import { useOrganizationAccessTopicsQuery } from './useOrganizationAccessTopicsQuery';

export type UserOrganization = {
  id: string;
  name: string;
  sector: string;
  webSearchEnabled: boolean;
  projectFilesEnabled: boolean;
  taxNotesContentViewerEnabled: boolean;
};

export type UserContextValue = {
  user: User;
  sessionValid: boolean;
  updateUserPreferences: (data: Partial<Preferences>) => Promise<UserPreferences>;
  subscriptionSettings?: SubscriptionSettings;
  subscriptionSettingsStatus?: QueryStatus;
  refetchSubscriptionSettings: ReturnType<typeof useSubscriptionSettingsQuery>['refetch'];
  userOrganization?: UserOrganization;
  topics: Topic[];
  organizationAccessTopicsAccess?: QueryStatus;
}

export const UserContext = React.createContext<UserContextValue | null>(null);
UserContext.displayName = 'UserContext';

type Props = {
  user: User;
  userOrganization?: UserOrganization;
}

export function UserContextProvider({ children, user, userOrganization }: React.PropsWithChildren<Props>) {
  const { updateUserPreferences } = useUpdateUserPreferencesQuery();
  const { data: subscriptionSettings, status: subscriptionSettingsStatus, refetch: refetchSubscriptionSettings } = useSubscriptionSettingsQuery();
  const { data: organizationAccessTopics, status: organizationAccessTopicsAccess } = useOrganizationAccessTopicsQuery();
  const { sentry } = useSentryContext();
  const { api, projectApi, sourceApi, threadApi, interactionsApi } = useAskBluejApiContext();
  const [sessionValid, setSessionValid] = useState(true);
  const value = {
    user,
    updateUserPreferences,
    sessionValid,
    subscriptionSettings,
    subscriptionSettingsStatus,
    refetchSubscriptionSettings,
    userOrganization,
    topics: organizationAccessTopics?.topics ?? [],
    organizationAccessTopicsAccess
  }

  React.useEffect(() => {
    sentry.setUserData({ email: user.email });
  }, [user]);

  React.useEffect(() => {
    function handleAuthenticationEvent() {
      setSessionValid(false);
    }
    const instances = [api, projectApi, sourceApi, threadApi, interactionsApi];
    instances.forEach(
      (instance) =>
        instance.emitter.addListener('session invalid', handleAuthenticationEvent)
    );
    return () => {
      instances.forEach(
        (instance) =>
          instance.emitter.removeListener('session invalid', handleAuthenticationEvent)
      );
    }
  }, []);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}
export const useUserContext = createUseContext(UserContext);

export function useIsUserAdmin() {
  const { user } = useUserContext();
  return user.isAdmin;
}

export function useIsOrganizationAdmin() {
  const { user } = useUserContext();
  // we assume that the user has only one organization
  return user.organizationRoles?.[0]?.roles.includes('admin') ?? false;
}

export function useIsUserReadonly() {
  const { subscriptionSettings } = useUserContext();
  return subscriptionSettings?.isReadonly ?? true;
}

export type TrialStatus = 'none' | 'active' | 'expired';

export function useUserTrialStatus(): { status: TrialStatus; daysUntilTrialExpiry: number } {
  const { subscriptionSettings } = useUserContext();

  if (
    !subscriptionSettings ||
    !subscriptionSettings.manageable ||
    !('state' in subscriptionSettings) ||
    subscriptionSettings.state !== "trial" ||
    ('cardAttached' in subscriptionSettings && subscriptionSettings.cardAttached) ||
    ('isConverted' in subscriptionSettings && subscriptionSettings.isConverted) ||
    !subscriptionSettings.endDate
  ) {
    return {
      status: 'none',
      daysUntilTrialExpiry: 0
    };
  }

  const timeUntilTrialExpiryMs = dayjs(subscriptionSettings.endDate).diff(dayjs(), 'ms');

  return {
    status: timeUntilTrialExpiryMs > 0 ? 'active' : 'expired',
    daysUntilTrialExpiry: Math.ceil(timeUntilTrialExpiryMs / (1000 * 60 * 60 * 24))
  };
}

export function useUserTheme() {
  const preferences = useUserPreferencesLoaderContext();
  const [theme, setTheme] = useState(preferences.preferredThemeOption ?? 'system');

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (preferences.preferredThemeOption === 'system') {
        setTheme(mediaQuery.matches ? 'dark' : 'light');
      } else if (preferences.preferredThemeOption) {
        setTheme(preferences.preferredThemeOption);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    handleChange();

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [preferences]);

  return theme;
}
